// src/modules/finan/pages/NFSETab.tsx
import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useDebounce, useToast } from "../../../../shared/hooks";
import { FinancialService } from "../../../../shared/services/api/financial/FinancialService";
import { TableNfse } from "../TableNfse";
import { ToolbarNFSE } from "../ToolbarNfse";

export const NFSETab = () => {
	const toastHandler = useToast();
	const { debounce } = useDebounce();

	const [textSearch, setTextSearch] = useState("");
	const [dataIni, setDataIni] = useState("");
	const [dataFim, setDataFim] = useState("");
	const [status, setStatus] = useState("todos");
	const [partnerId, setPartnerId] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [nfseData, setNfseData] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [refreshTrigger, setRefreshTrigger] = useState(0);

	const [selectedGenerate, setSelectedGenerate] = useState<string[]>([]);

	const normalizeStatus = (raw: string) => (raw === "todos" ? "Todos" : raw);

	const handleFetchData = (currentPage: number, limit: number, search: string, ini: string, fim: string, stat: string, partner: string) => {
		setIsLoading(true);
		const finalStatus = normalizeStatus(stat);

		debounce(() => {
			FinancialService.getNfse(currentPage + 1, search, "Todos", ini, fim, partner, limit, finalStatus)
				.then((response) => {
					if (response instanceof Error) {
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title="Erro" content="Erro ao buscar NFSE" />,
						});
					} else {
						setNfseData(response.data);
						setTotalCount(response.totalCount ?? 0);
					}
				})
				.finally(() => setIsLoading(false));
		});
	};

	useEffect(() => {
		const defaultDataIni = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0];
		const defaultDataFim = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split("T")[0];

		setTextSearch("");
		setDataIni(defaultDataIni);
		setDataFim(defaultDataFim);
		setStatus("todos");
		handleFetchData(0, rowsPerPage, "", defaultDataIni, defaultDataFim, "todos", partnerId);
	}, [refreshTrigger]);

	const handleRefresh = () => setRefreshTrigger((prev) => prev + 1);

	const handleSearch = () => {
		setPage(0);
		handleFetchData(0, rowsPerPage, textSearch, dataIni, dataFim, status, partnerId);
	};

	const handleChangePage = (newPage: number) => {
		setPage(newPage);
		handleFetchData(newPage, rowsPerPage, textSearch, dataIni, dataFim, status, partnerId);
	};

	const handleChangeRowsPerPage = (newRowsPerPage: number) => {
		setRowsPerPage(newRowsPerPage);
		setPage(0);
	};

	const isNFSelectable = (row: any) => {
		const canEmit = row.Installment_status === "paid" && (!row.nfse_id || row.nfse_id === "");
		const canReprocess = status === "unprocessed" && row.nfse_status === "unprocessed" && Boolean(row.nfse_id) && row.nfse_gateway === "GRIDWEBTI";

		return canEmit || canReprocess;
	};

	const handleToggleGenerateCheckbox = (id: string) => {
		setSelectedGenerate((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
	};

	const handleToggleAllGenerate = (checked: boolean) => {
		if (checked) {
			const ids = nfseData.filter(isNFSelectable).map((row) => row.Installment_id);
			setSelectedGenerate(ids);
		} else {
			setSelectedGenerate([]);
		}
	};

	const generalCheckboxSelected =
		nfseData.filter(isNFSelectable).length > 0 && nfseData.filter(isNFSelectable).every((row) => selectedGenerate.includes(row.Installment_id));

	return (
		<Box>
			<ToolbarNFSE
				textSearch={textSearch}
				onChangeTextSearch={setTextSearch}
				onClearTextSearch={() => setTextSearch("")}
				dataIni={dataIni}
				dataFim={dataFim}
				onChangeDateStart={setDataIni}
				onChangeDateEnd={setDataFim}
				status={status}
				onChangeStatus={setStatus}
				partnerId={partnerId}
				onChangePartner={setPartnerId}
				onClickRefresh={handleSearch}
			/>

			<LoadingComp isopen={isLoading} />
			<Box sx={{ overflowX: "auto" }}>
				<TableNfse
					rows={nfseData}
					page={page}
					rowsPerPage={rowsPerPage}
					totalCount={totalCount}
					generalCheckboxSelected={generalCheckboxSelected}
					isNFSelectable={isNFSelectable}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
					onToggleGeneralCheckbox={handleToggleAllGenerate}
					onToggleRowCheckbox={handleToggleGenerateCheckbox}
					isLoading={isLoading}
					selectedRows={selectedGenerate}
					currentStatus={status}
				/>
			</Box>
		</Box>
	);
};
